import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { amDesignColors, amDesignTypography, amDesignComponents } from '../../themes/amDesignSystem';
import { useBusiness } from '../../contexts/BusinessContext';
import {
    Box, Typography, Card, CardContent, Grid, Avatar, CircularProgress, Alert, Button,
    TextField, FormControl, InputLabel, Select, MenuItem, Chip, LinearProgress, Divider, Tabs, Tab,
    Collapse, IconButton, Pagination, Autocomplete
} from '@mui/material';
import { Search, Refresh, ExpandMore, ExpandLess, TrendingUp, TrendingDown, OpenInNew } from '@mui/icons-material';

const getRankBadgeColor = (rank) => {
    if (!rank) return amDesignColors.text.tertiary;
    if (rank === 1) return '#ffd700';
    if (rank <= 3) return '#c0c0c0';
    if (rank <= 10) return '#cd7f32';
    return amDesignColors.success;
};

const getPerformanceLevel = (improvement) => {
    if (improvement > 5) return { label: 'Ausgezeichnet', color: amDesignColors.success };
    if (improvement > 2) return { label: 'Sehr gut', color: '#66bb6a' };
    if (improvement > 0) return { label: 'Gut', color: '#81c784' };
    if (improvement === 0) return { label: 'Stabil', color: '#ffb74d' };
    return { label: 'Rückgang', color: '#ef5350' };
};

// Calculate percentage improvement
const getPercentageImprovement = (beforeRank, afterRank) => {
    if (!beforeRank || beforeRank === 0) return 0;
    return Math.round(((beforeRank - afterRank) / beforeRank) * 100);
};

// Rank quality score (0-100, higher is better)
const getRankQualityScore = (rank) => {
    if (!rank) return 0;
    if (rank === 1) return 100;
    if (rank <= 3) return 95;
    if (rank <= 5) return 90;
    if (rank <= 10) return 75;
    if (rank <= 15) return 60;
    if (rank <= 20) return 40;
    return 20;
};

// Weighted performance score (0-100)
const getPerformanceScore = (beforeRank, afterRank) => {
    if (!beforeRank || !afterRank) return 0;
    const percentageImprovement = getPercentageImprovement(beforeRank, afterRank);
    const startingQuality = getRankQualityScore(beforeRank);
    const finalQuality = getRankQualityScore(afterRank);

    return Math.round(
        (percentageImprovement * 0.4) +
        (startingQuality * 0.3) +
        (finalQuality * 0.3)
    );
};

// Get impact label based on score
const getImpactLabel = (score) => {
    if (score >= 80) return 'Hot';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Low';
};

// Get impact color based on score
const getImpactColor = (score) => {
    if (score >= 80) return '#ff5722';
    if (score >= 60) return amDesignColors.success;
    if (score >= 40) return amDesignColors.warning;
    return amDesignColors.error;
};

// Format date in German format (DD.MM.YYYY)
const formatDateDE = (dateValue) => {
    if (!dateValue) return '';
    // Handle both Date objects and strings
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (isNaN(date.getTime())) return ''; // Invalid date
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
};

const ImprovementChip = ({ value, size = 'small' }) => (
    <Chip
        icon={value > 0 ? <TrendingUp style={{ fontSize: 14 }} /> : value < 0 ? <TrendingDown style={{ fontSize: 14 }} /> : undefined}
        label={`${value > 0 ? '+' : ''}${value}`}
        size={size}
        sx={{
            background: value > 0
                ? 'linear-gradient(135deg, #e8f5e8, #c8e6c9)'
                : value < 0
                ? 'linear-gradient(135deg, #ffebee, #ffcdd2)'
                : 'linear-gradient(135deg, #f5f5f5, #eeeeee)',
            color: value > 0 ? '#2e7d32' : value < 0 ? '#c62828' : '#616161',
            fontWeight: 'bold',
            border: 'none',
            fontSize: '0.7rem',
            height: 22,
        }}
    />
);

const RankBadge = ({ rank, size = 44 }) => (
    <Box sx={{
        width: size, height: size, borderRadius: '50%',
        background: getRankBadgeColor(rank),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: amDesignColors.white, fontWeight: 'bold', fontSize: size > 36 ? '1rem' : '0.8rem',
        flexShrink: 0,
    }}>
        {rank || '—'}
    </Box>
);

const cardBase = {
    borderRadius: '16px',
    background: 'linear-gradient(135deg, amDesignColors.ghost 0%, amDesignColors.white 100%)',
    border: '1px solid amDesignColors.border',
    position: 'relative',
    overflow: 'visible',
};

const topBar = (color) => ({
    '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0,
        height: '4px', background: color,
        borderTopLeftRadius: '16px', borderTopRightRadius: '16px',
    }
});

// ─── TOKEN HELPER ────────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem('auth_token') || localStorage.getItem('token');

// ─── MONTHS SELECTOR ─────────────────────────────────────────────────────────
const MonthsSelect = ({ value, onChange, disabled }) => (
    <FormControl sx={{ minWidth: 150 }}>
        <InputLabel>Zeitraum</InputLabel>
        <Select value={value} label="Zeitraum" onChange={e => onChange(e.target.value)} disabled={disabled}>
            <MenuItem value={1}>1 Monat</MenuItem>
            <MenuItem value={2}>2 Monate</MenuItem>
            <MenuItem value={3}>3 Monate</MenuItem>
            <MenuItem value={6}>6 Monate</MenuItem>
            <MenuItem value={12}>12 Monate</MenuItem>
        </Select>
    </FormControl>
);

// Reason why this keyword is a top performer
const getRankLabel = (rank) => {
    if (!rank) return null;
    if (rank === 1) return { label: '🥇 Platz 1', color: '#f57f17' };
    if (rank <= 3) return { label: `🥈 Top 3 (Rang ${rank})`, color: amDesignColors.text.tertiary };
    if (rank <= 5) return { label: `Top 5 (Rang ${rank})`, color: '#6d4c41' };
    if (rank <= 10) return { label: `Top 10 (Rang ${rank})`, color: '#2e7d32' };
    return null;
};

// ─── KEYWORD ROW (used in Nach Unternehmen and Übersicht business expand) ────
const KeywordRow = ({ kw, last }) => {
    const perf = getPerformanceLevel(kw.improvement);
    const rankLabel = getRankLabel(kw.currentRank);
    const percentageImprovement = kw.previousRank ? getPercentageImprovement(kw.previousRank, kw.currentRank) : 0;
    const performanceScore = kw.previousRank ? getPerformanceScore(kw.previousRank, kw.currentRank) : 0;

    return (
        <>
            <Box display="flex" alignItems="flex-start" gap={1.5} py={1.5}>
                <RankBadge rank={kw.currentRank} size={40} />
                <Box flex={1} minWidth={0}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: amDesignColors.text.primary, mb: 0.25 }} noWrap>
                        {kw.keyword}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                        {kw.previousRank
                            ? `Rang ${kw.previousRank}(${formatDateDE(kw.previousRankDate)}) → Rang ${kw.currentRank}(${formatDateDE(kw.currentRankDate)})`
                            : `Aktuell Rang ${kw.currentRank} · kein Vergleichswert`}
                        {kw.dataPoints ? ` · ${kw.dataPoints} Messpunkte` : ''}
                    </Typography>
                    {/* Reason badges */}
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                        {kw.improvement > 0 && (
                            <Chip
                                label={`↑ Verbessert um ${kw.improvement} Plätze`}
                                size="small"
                                sx={{ background: '#e8f5e8', color: '#2e7d32', fontWeight: 600, fontSize: '0.65rem', height: 18 }}
                            />
                        )}
                        {rankLabel && (
                            <Chip
                                label={rankLabel.label}
                                size="small"
                                sx={{ background: `${rankLabel.color}18`, color: rankLabel.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }}
                            />
                        )}
                        {kw.improvement === 0 && !rankLabel && (
                            <Chip label={perf.label} size="small" sx={{ background: `${perf.color}20`, color: perf.color, fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
                        )}
                    </Box>
                </Box>
                {/* OPTION A: Percentage improvement and performance score */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.75 }}>
                    {percentageImprovement > 0 && (
                        <Chip
                            label={`↑${percentageImprovement}%`}
                            size="small"
                            sx={{
                                background: percentageImprovement > 30 ? amDesignColors.success : amDesignColors.warning,
                                color: amDesignColors.white,
                                fontWeight: 'bold',
                                fontSize: '0.75rem'
                            }}
                        />
                    )}
                </Box>
                <ImprovementChip value={kw.improvement} />
            </Box>
            {!last && <Divider />}
        </>
    );
};

// ─── BUSINESS ROW (used in Nach Keyword and Übersicht keyword expand) ────────
const BusinessRow = ({ biz, keyword, last }) => (
    <>
        <Box display="flex" alignItems="center" gap={1.5} py={1}>
            <RankBadge rank={biz.currentRank} size={36} />
            <Box flex={1} minWidth={0}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: amDesignColors.text.primary }} noWrap>
                    {biz.businessName}
                </Typography>
                {keyword && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                        Keyword: {keyword}
                    </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                    {biz.previousRank ? `Rang ${biz.previousRank}(${formatDateDE(biz.previousRankDate)}) → ${biz.currentRank}(${formatDateDE(biz.currentRankDate)})` : `Aktuell Rang ${biz.currentRank}`}
                    {biz.dataPoints ? ` · ${biz.dataPoints} Datenpunkte` : ''}
                </Typography>
            </Box>
            <ImprovementChip value={biz.improvement} />
        </Box>
        {!last && <Divider />}
    </>
);

// ─── EXPANDABLE SECTION CARD ─────────────────────────────────────────────────
const ExpandableCard = ({ title, subtitle, chipLabel, chipColor, topBarGradient, expanded, onToggle, children }) => (
    <Card sx={{ ...cardBase, ...topBar(topBarGradient), mb: 2 }}>
        <CardContent sx={{ p: 2.5 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1} minWidth={0}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: amDesignColors.text.primary }} noWrap>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={1} ml={1}>
                    {chipLabel && (
                        <Chip label={chipLabel} size="small" sx={{ background: `${chipColor}20`, color: chipColor, fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
                    )}
                    <IconButton size="small" onClick={onToggle}>
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box mt={1.5}>
                    {children}
                </Box>
            </Collapse>
        </CardContent>
    </Card>
);

// In-memory cache for keyword performance data (no localStorage quota issues)
const apiCache = new Map();

// ════════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════════
const KeywordPerformancePage = () => {
    const { selectedBusiness } = useBusiness();
    const [activeTab, setActiveTab] = useState(0);

    // Tab 0: Overview — all 4 periods pre-loaded from cache
    const [overviewLoading, setOverviewLoading]   = useState(false);
    const [allPeriods, setAllPeriods]             = useState({});   // { 1: data, 3: data, 6: data, 12: data }
    const [overviewMonths, setOverviewMonths]     = useState(3);
    const [overviewError, setOverviewError]       = useState(null);
    const [expandedBiz, setExpandedBiz]           = useState({});
    const [expandedKw, setExpandedKw]             = useState({});
    const [topBusinessesLimit, setTopBusinessesLimit] = useState(10);
    const [topKeywordsLimit, setTopKeywordsLimit] = useState(20);

    // Derived: current period data based on selected months
    const overviewData = allPeriods[overviewMonths] || null;

    // Tab 1: Nach Unternehmen
    const [busName, setBusName]           = useState('');
    const [busMonths, setBusMonths]       = useState(3);
    const [busLoading, setBusLoading]     = useState(false);
    const [busData, setBusData]           = useState(null);
    const [busError, setBusError]         = useState(null);
    const [busSuggestions, setBusSuggestions] = useState([]);

    // Tab 2: Nach Keyword
    const [kwKeyword, setKwKeyword]   = useState('');
    const [kwMonths, setKwMonths]     = useState(3);
    const [kwLoading, setKwLoading]   = useState(false);
    const [kwError, setKwError]       = useState(null);
    const [kwResults, setKwResults]   = useState(null);
    const [kwPage, setKwPage]         = useState(1);
    const [kwSuggestions, setKwSuggestions] = useState([]);
    const KW_PER_PAGE = 12;

    // Fetch all businesses and keywords on component mount
    useEffect(() => {
        const fetchSuggestions = async () => {
            const token = getToken();
            if (!token) return;

            try {
                const [busRes, kwRes] = await Promise.all([
                    fetch('/api/analytics/all-businesses', { headers: { Authorization: `Bearer ${token}` } }),
                    fetch('/api/analytics/all-keywords', { headers: { Authorization: `Bearer ${token}` } })
                ]);

                if (busRes.ok) {
                    const busData = await busRes.json();
                    setBusSuggestions(busData.businesses || []);
                }

                if (kwRes.ok) {
                    const kwData = await kwRes.json();
                    setKwSuggestions(kwData.keywords || []);
                }
            } catch (err) {
                console.error('Error fetching suggestions:', err);
            }
        };

        fetchSuggestions();
    }, []); // eslint-disable-line

    const businessOptions = busSuggestions;
    const keywordOptions = kwSuggestions;

    // Custom filter for autocomplete (case-insensitive substring matching)
    const createFilterOptions = () => (options, state) => {
        const inputValue = state.inputValue.toLowerCase().trim();
        if (!inputValue) return options;
        return options.filter(option => option.toLowerCase().includes(inputValue));
    };

    // Load all periods once on component mount (check in-memory cache first)
    useEffect(() => {
        if (Object.keys(allPeriods).length === 0) {
            if (apiCache.has('keywordPerfCache')) {
                setAllPeriods(apiCache.get('keywordPerfCache'));
            } else {
                loadOverview();
            }
        }
    }, []); // eslint-disable-line


    const loadOverview = useCallback(async () => {
        setOverviewLoading(true);
        setOverviewError(null);
        const token = getToken();
        if (!token) { setOverviewError('Authentifizierung erforderlich.'); setOverviewLoading(false); return; }
        try {
            // Fetch all 4 periods in parallel — all served from cache instantly
            const periods = [1, 3, 6, 12];
            const results = await Promise.all(
                periods.map(m =>
                    fetch(`/api/analytics/top-keywords-overview?months=${m}`, { headers: { Authorization: `Bearer ${token}` } })
                        .then(r => r.ok ? r.json() : null)
                        .catch(() => null)
                )
            );
            const periodMap = {};
            periods.forEach((m, i) => { if (results[i]) periodMap[m] = results[i]; });
            setAllPeriods(periodMap);
            // Cache in memory (instant, no quota issues)
            apiCache.set('keywordPerfCache', periodMap);
        } catch (e) { setOverviewError(e.message); }
        finally { setOverviewLoading(false); }
    }, []);

    const loadBusiness = useCallback(async () => {
        if (!busName.trim()) return;
        setBusLoading(true); setBusError(null); setBusData(null);
        const token = getToken();
        if (!token) { setBusError('Authentifizierung erforderlich.'); setBusLoading(false); return; }
        try {
            const res = await fetch(`/api/analytics/business-keyword-performance?businessName=${encodeURIComponent(busName.trim())}&months=${busMonths}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Fehler'); }
            setBusData(await res.json());
        } catch (e) { setBusError(e.message); }
        finally { setBusLoading(false); }
    }, [busName, busMonths]);

    const loadKeyword = useCallback(async () => {
        if (!kwKeyword.trim()) return;
        setKwLoading(true); setKwError(null); setKwResults(null); setKwPage(1);
        const token = getToken();
        if (!token) { setKwError('Authentifizierung erforderlich.'); setKwLoading(false); return; }
        try {
            const res = await fetch(`/api/analytics/keyword-performance?keyword=${encodeURIComponent(kwKeyword.trim())}&months=${kwMonths}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Fehler'); }
            setKwResults(await res.json());
        } catch (e) { setKwError(e.message); }
        finally { setKwLoading(false); }
    }, [kwKeyword, kwMonths]);

    const toggleBiz = (key) => setExpandedBiz(prev => ({ ...prev, [key]: !prev[key] }));
    const toggleKw  = (key) => setExpandedKw(prev => ({ ...prev, [key]: !prev[key] }));

    // ── RENDER ──────────────────────────────────────────────────────────────
    return (
        <Box sx={{ p: 3, mx: 'auto', background: '#F7F6F3', minHeight: '100vh' }}>
            {/* Header */}
            <Typography
              variant="caption"
              sx={{
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.05em',
                color: amDesignColors.primary,
                fontFamily: 'Plus Jakarta Sans, system-ui',
                textTransform: 'uppercase',
                mb: 0.5
              }}
            >
              Rankings
            </Typography>

            <Typography
              sx={{
                fontSize: '32px',
                fontWeight: 800,
                letterSpacing: '-0.025em',
                color: amDesignColors.text.primary,
                fontFamily: amDesignTypography.fontFamily.heading,
                mb: 0.5
              }}
            >
              Keyword-Performance
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: amDesignColors.text.secondary,
                fontFamily: amDesignTypography.fontFamily.body,
                mb: 3
              }}
            >
              Ranking-Verbesserungen nach Unternehmen oder Keyword analysieren
            </Typography>
            

           

            {/* Tabs and Time Period Selector */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 3, pb: 0, borderBottom: '1px solid #ECEBEE' }}>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  sx={{
                    flex: 1,
                    '& .MuiTabs-indicator': {
                      backgroundColor: amDesignColors.primary,
                      height: 3
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '15px',
                      color: amDesignColors.text.secondary,
                      padding: '8px 16px'
                    },
                    '& .Mui-selected': {
                      color: amDesignColors.text.primary,
                      fontWeight: 800
                    }
                  }}
                >
                    <Tab label="Übersicht" />
                    <Tab label="Nach Unternehmen" />
                    <Tab label="Nach Keyword" />
                </Tabs>

                {/* Time Period Segmented Control */}
                <Box sx={{
                  display: 'inline-flex',
                  gap: 0,
                  border: `1px solid ${amDesignColors.border}`,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#FFFFFF'
                }}>
                    {[1,3, 12].map((months, idx) => {
                        const isActive = (activeTab === 0 && overviewMonths === months) ||
                                       (activeTab === 1 && busMonths === months) ||
                                       (activeTab === 2 && kwMonths === months);
                        const handleClick = () => {
                            if (activeTab === 0) setOverviewMonths(months);
                            else if (activeTab === 1) setBusMonths(months);
                            else if (activeTab === 2) setKwMonths(months);
                        };
                        return (
                            <Button
                                key={months}
                                size="small"
                                variant="text"
                                onClick={handleClick}
                                sx={{
                                  fontWeight: isActive ? 600 : 400,
                                  textTransform: 'none',
                                  minWidth: 'auto',
                                  px: '16px',
                                  py: '8px',
                                  fontSize: '13px',
                                  color: isActive ? amDesignColors.text.primary : amDesignColors.text.secondary,
                                  backgroundColor: isActive ? '#f0f0f0ff' : 'transparent',
                                  borderRadius: 0,
                                  borderRight: idx < 1 ? `1px solid ${amDesignColors.border}` : 'none',
                                  '&:hover': {
                                    backgroundColor: isActive ? '#F5F0F0' : '#FAFAFA'
                                  }
                                }}
                            >
                                {months} Monate
                            </Button>
                        );
                    })}
                </Box>
            </Box>

            <Box>

                    {/* ═══════════════════════════════ TAB 0: ÜBERSICHT ═══════════════════════════ */}
                    {activeTab === 0 && (
                        <Box>
                            {overviewError && <Alert severity="error" sx={{ mb: 2 }}>{overviewError}</Alert>}

                            {overviewLoading && (
                                <Box sx={{ textAlign: 'center', py: 5 }}>
                                    <CircularProgress size={56} />
                                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Lade Übersichtsdaten...</Typography>
                                </Box>
                            )}

                            {!overviewLoading && overviewData && (
                                <Box>
                                    

                                    {/* Business List */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                                        {overviewData.topBusinesses.length === 0 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                                Keine Unternehmen mit Verbesserungen im gewählten Zeitraum.
                                            </Typography>
                                        )}
                                        {overviewData.topBusinesses.map((biz, i) => {
                                            const key = `biz-${i}`;
                                            const positiveCount = biz.positiveCount ?? (biz.keywords || []).filter(k => k.improvement > 0).length;
                                            return (
                                                <Box key={key}>
                                                    <Card
                                                        sx={{
                                                            background: '#FFFFFF',
                                                            border: '1px solid #ECEBEE',
                                                            borderRadius: '14px',
                                                            boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            p: '18px 20px',
                                                            gap: 2,
                                                            width: '100%',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                boxShadow: '0 2px 6px rgba(16,16,20,.1), 0 4px 12px rgba(16,16,20,.08)'
                                                            }
                                                        }}
                                                    >
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{
                                                                fontSize: '15px',
                                                                fontWeight: 700,
                                                                color: '#0A0A0B',
                                                                fontFamily: 'Plus Jakarta Sans, system-ui',
                                                                mb: 0.5
                                                            }}>
                                                                {biz.businessName}
                                                            </Typography>
                                                            <Typography sx={{
                                                                fontSize: '13px',
                                                                fontWeight: 500,
                                                                color: '#55555D',
                                                                fontFamily: 'Plus Jakarta Sans, system-ui'
                                                            }}>
                                                                {biz.keywordCount} Keywords · {positiveCount} verbessert · Bester +{biz.bestImprovement}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 'fit-content' }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography sx={{
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    letterSpacing: '0.05em',
                                                                    color: '#55555D',
                                                                    textTransform: 'uppercase',
                                                                    fontFamily: 'Plus Jakarta Sans, system-ui'
                                                                }}>
                                                                    Ø Verbesserung
                                                                </Typography>
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 0.5,
                                                                    backgroundColor: '#E8F5F0',
                                                                    padding: '6px 12px',
                                                                    borderRadius: '6px'
                                                                }}>
                                                                    <TrendingUp sx={{ color: '#1E9E5A', fontSize: 16 }} />
                                                                    <Typography sx={{
                                                                        fontSize: '14px',
                                                                        fontWeight: 700,
                                                                        color: '#1E9E5A',
                                                                        fontFamily: 'JetBrains Mono, monospace'
                                                                    }}>
                                                                        +{biz.avgImprovement}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            <IconButton
                                                                size="small"
                                                                sx={{ color: '#55555D' }}
                                                                onClick={() => toggleBiz(key)}
                                                            >
                                                                {expandedBiz[key] ? <ExpandLess /> : <ExpandMore />}
                                                            </IconButton>
                                                        </Box>
                                                    </Card>
                                                    <Collapse in={!!expandedBiz[key]}>
                                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                                            {(biz.keywords || []).map((kw, ki) => (
                                                                <Grid item xs={12} sm={6} md={4} key={ki}>
                                                                    <Card sx={{
                                                                        background: '#FFFFFF',
                                                                        border: '1px solid #ECEBEE',
                                                                        borderRadius: '14px',
                                                                        padding: '16px',
                                                                        boxShadow: '0 1px 2px rgba(16,16,20,.06), 0 2px 6px rgba(16,16,20,.05)',
                                                                        display: 'flex',
                                                                        flexDirection: 'column',
                                                                        gap: '12px',
                                                                        height: '100%',
                                                                        transition: 'all 0.2s ease',
                                                                        '&:hover': {
                                                                            transform: 'translateY(-2px)',
                                                                            boxShadow: '0 2px 4px rgba(16,16,20,.05), 0 6px 16px rgba(16,16,20,.08)'
                                                                        }
                                                                    }}>
                                                                        <Box>
                                                                            <Typography sx={{
                                                                                fontSize: '14px',
                                                                                fontWeight: 700,
                                                                                color: '#0A0A0B',
                                                                                mb: 0.5,
                                                                                fontFamily: 'Plus Jakarta Sans, system-ui'
                                                                            }}>
                                                                                {kw.keyword}
                                                                            </Typography>
                                                                            <Typography sx={{
                                                                                fontSize: '12px',
                                                                                color: '#55555D',
                                                                                fontFamily: 'Plus Jakarta Sans, system-ui'
                                                                            }}>
                                                                                {kw.previousRank ? `Rang ${kw.previousRank} → ${kw.currentRank}` : `Aktuell Rang ${kw.currentRank}`}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                                            <Typography sx={{
                                                                                fontSize: '11px',
                                                                                fontWeight: 600,
                                                                                letterSpacing: '0.05em',
                                                                                color: '#55555D',
                                                                                textTransform: 'uppercase',
                                                                                fontFamily: 'Plus Jakarta Sans, system-ui'
                                                                            }}>
                                                                                Ø Verbesserung
                                                                            </Typography>
                                                                            {kw.improvement !== undefined && (
                                                                                <Box sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                    backgroundColor: kw.improvement > 0 ? '#E8F5F0' : kw.improvement < 0 ? '#FDE8EB' : '#F5F5F5',
                                                                                    padding: '4px 10px',
                                                                                    borderRadius: '6px'
                                                                                }}>
                                                                                    {kw.improvement > 0 && <TrendingUp sx={{ color: '#1E9E5A', fontSize: 14 }} />}
                                                                                    {kw.improvement < 0 && <TrendingDown sx={{ color: '#C80F2E', fontSize: 14 }} />}
                                                                                    <Typography sx={{
                                                                                        fontSize: '13px',
                                                                                        fontWeight: 700,
                                                                                        color: kw.improvement > 0 ? '#1E9E5A' : kw.improvement < 0 ? '#C80F2E' : '#55555D',
                                                                                        fontFamily: 'JetBrains Mono, monospace'
                                                                                    }}>
                                                                                        {kw.improvement > 0 ? `+${kw.improvement}` : kw.improvement}
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                        </Box>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            endIcon={<OpenInNew fontSize="small" />}
                                                                            onClick={() => window.open(`/ranking/${biz.googleLocationId}?keyword=${encodeURIComponent(kw.keyword)}`, '_blank')}
                                                                            sx={{
                                                                                fontSize: '12px',
                                                                                fontWeight: 600,
                                                                                textTransform: 'none',
                                                                                color: amDesignColors.primary,
                                                                                borderColor: amDesignColors.primary,
                                                                                '&:hover': {
                                                                                    borderColor: amDesignColors.primary,
                                                                                    backgroundColor: 'rgba(200, 15, 46, 0.04)'
                                                                                }
                                                                            }}
                                                                        >
                                                                            View Ranking
                                                                        </Button>
                                                                    </Card>
                                                                </Grid>
                                                            ))}
                                                        </Grid>
                                                    </Collapse>
                                                </Box>
                                            );
                                        })}
                                    </Box>                                </Box>
                            )}

                            {!overviewLoading && !overviewData && !overviewError && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6">Klicken Sie auf "Aktualisieren" um Daten zu laden.</Typography>
                                </CardContent></Card>
                            )}
                        </Box>
                    )}

                    {/* ═══════════════════════════ TAB 1: NACH UNTERNEHMEN ════════════════════════ */}
                    {activeTab === 1 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 3 }}>
                                <Autocomplete
                                    freeSolo
                                    value={busName}
                                    onChange={(_, newValue) => setBusName(newValue || '')}
                                    inputValue={busName}
                                    onInputChange={(_, newInputValue) => setBusName(newInputValue)}
                                    options={businessOptions}
                                    filterOptions={createFilterOptions()}
                                    sx={{ flex: 1, minWidth: 250 }}
                                    disabled={busLoading}
                                    noOptionsText="Keine Unternehmen gefunden"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Unternehmensname"
                                            placeholder="z.B. Müller GmbH"
                                            onKeyDown={e => e.key === 'Enter' && loadBusiness()}
                                        />
                                    )}
                                    ListboxProps={{ style: { maxHeight: '300px' } }}
                                />
                                <MonthsSelect value={busMonths} onChange={v => setBusMonths(v)} disabled={busLoading} />
                                <Button variant="contained" startIcon={busLoading ? <CircularProgress size={18} color="inherit" /> : <Search />} onClick={loadBusiness} disabled={busLoading || !busName.trim()}>
                                    Suchen
                                </Button>
                            </Box>

                            {busError && <Alert severity="error" sx={{ mb: 2 }}>{busError}</Alert>}

                            {busLoading && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 5 }}>
                                    <CircularProgress size={56} />
                                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Suche Unternehmen...</Typography>
                                </CardContent></Card>
                            )}

                            {!busLoading && busData && (
                                <>
                                    {/* Business summary header */}
                                    <Card sx={{ ...cardBase, ...topBar('linear-gradient(90deg, amDesignColors.primary, #42a5f5)'), mb: 3 }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Typography variant="h5" sx={{ fontWeight: 700, color: amDesignColors.text.primary, mb: 1 }}>
                                                {busData.businessName}
                                            </Typography>
                                            <Box display="flex" gap={1.5} flexWrap="wrap">
                                                <Chip label={`${busData.keywordCount} Keywords`} size="small" sx={{ background: '#e3f2fd', color: '#1565c0', fontWeight: 600 }} />
                                                <Chip label={`Ø +${busData.avgImprovement}`} size="small" sx={{ background: '#e8f5e8', color: '#2e7d32', fontWeight: 600 }} />
                                                <Chip label={`Best: +${busData.bestImprovement}`} size="small" sx={{ background: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                                                <Chip label={`${busMonths} Monate`} size="small" variant="outlined" />
                                            </Box>
                                        </CardContent>
                                    </Card>

                                    {/* Keyword cards */}
                                    <Grid container spacing={2}>
                                        {busData.keywords.map((kw, idx) => {
                                            const perf = getPerformanceLevel(kw.improvement);
                                            return (
                                                <Grid item xs={12} sm={6} lg={4} key={kw.keyword}>
                                                    <Card sx={{
                                                        ...cardBase,
                                                        ...topBar(kw.improvement > 0 ? 'linear-gradient(90deg, amDesignColors.success, #66bb6a)' : kw.improvement < 0 ? 'linear-gradient(90deg, amDesignColors.error, #ef5350)' : 'linear-gradient(90deg, amDesignColors.text.tertiary, amDesignColors.border)'),
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        border: `1px solid ${kw.improvement > 0 ? '#e8f5e8' : kw.improvement < 0 ? '#ffebee' : amDesignColors.divider}`,
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': { boxShadow: '0 12px 28px rgba(0,0,0,0.12)', transform: 'translateY(-4px)' }
                                                    }}>
                                                        <CardContent sx={{ p: 2.5, flex: 1 }}>
                                                            {/* Keyword name */}
                                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                                                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', color: amDesignColors.text.primary, flex: 1, mr: 1 }}>
                                                                    {kw.keyword}
                                                                </Typography>
                                                                <ImprovementChip value={kw.improvement} />
                                                            </Box>

                                                            <Chip label={perf.label} size="small" sx={{ background: `${perf.color}20`, color: perf.color, fontWeight: 600, fontSize: '0.68rem', height: 20, mb: 2 }} />

                                                            {/* Rank comparison */}
                                                            <Box sx={{ background: 'linear-gradient(135deg, amDesignColors.paper, #f5f5f5)', borderRadius: '10px', p: 2, border: '1px solid #eaeaea', mb: 2 }}>
                                                                <Grid container spacing={1}>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            {busMonths}M zuvor
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={kw.previousRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            Aktuell
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={kw.currentRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>

                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={Math.min(Math.abs(kw.improvement) * 10, 100)}
                                                                sx={{ height: 6, borderRadius: 3, backgroundColor: amDesignColors.border, '& .MuiLinearProgress-bar': { background: kw.improvement > 0 ? 'linear-gradient(90deg, amDesignColors.success, #66bb6a)' : kw.improvement < 0 ? 'linear-gradient(90deg, amDesignColors.error, #ef5350)' : 'linear-gradient(90deg, amDesignColors.text.tertiary, amDesignColors.border)', borderRadius: 3 } }}
                                                            />
                                                            {kw.dataPoints > 0 && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block', textAlign: 'center', mt: 1 }}>
                                                                    {kw.dataPoints} Datenpunkte
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                        {busData.googleLocationId && (
                                                            <Box sx={{ borderTop: '1px solid #eee', p: 2, pt: 1.5, display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    endIcon={<OpenInNew fontSize="small" />}
                                                                    onClick={() => window.open(`/ranking/${busData.googleLocationId}?keyword=${encodeURIComponent(kw.keyword)}`, '_blank')}
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                >
                                                                    View Ranking
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </>
                            )}

                            {!busLoading && !busName && !busData && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Unternehmensname eingeben</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Geben Sie einen Unternehmensnamen ein, um alle Keywords und deren Verbesserungen zu sehen.
                                    </Typography>
                                </CardContent></Card>
                            )}
                        </Box>
                    )}

                    {/* ═══════════════════════════════ TAB 2: NACH KEYWORD ════════════════════════ */}
                    {activeTab === 2 && (
                        <Box>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', mb: 3 }}>
                                <Autocomplete
                                    freeSolo
                                    value={kwKeyword}
                                    onChange={(_, newValue) => setKwKeyword(newValue || '')}
                                    inputValue={kwKeyword}
                                    onInputChange={(_, newInputValue) => setKwKeyword(newInputValue)}
                                    options={keywordOptions}
                                    filterOptions={createFilterOptions()}
                                    sx={{ flex: 1, minWidth: 250 }}
                                    disabled={kwLoading}
                                    noOptionsText="Keine Keywords gefunden"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Keyword"
                                            placeholder="z.B. Badezimmerrenovierung"
                                            onKeyDown={e => e.key === 'Enter' && loadKeyword()}
                                        />
                                    )}
                                    ListboxProps={{ style: { maxHeight: '300px' } }}
                                />
                                <MonthsSelect value={kwMonths} onChange={v => setKwMonths(v)} disabled={kwLoading} />
                                <Button variant="contained" startIcon={kwLoading ? <CircularProgress size={18} color="inherit" /> : <Search />} onClick={loadKeyword} disabled={kwLoading || !kwKeyword.trim()}>
                                    Analysieren
                                </Button>
                            </Box>

                            {kwError && <Alert severity="error" sx={{ mb: 2 }}>{kwError}</Alert>}

                            {kwLoading && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 5 }}>
                                    <CircularProgress size={56} />
                                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Analysiere Ranking-Daten...</Typography>
                                </CardContent></Card>
                            )}

                            {kwResults && (
                                <Alert severity="success" sx={{ mb: 3 }}>
                                    <strong>{kwResults.total} Unternehmen</strong> mit Ranking-Daten für „<strong>{kwResults.keyword}</strong>" in den letzten <strong>{kwResults.months} Monaten</strong>
                                </Alert>
                            )}

                            {!kwLoading && kwResults && kwResults.businesses.length > 0 && (
                                <>
                                    <Grid container spacing={2} sx={{ mb: 3 }}>
                                        {kwResults.businesses.slice((kwPage - 1) * KW_PER_PAGE, kwPage * KW_PER_PAGE).map((biz, idx) => {
                                            const rankIdx = (kwPage - 1) * KW_PER_PAGE + idx;
                                            const perf = getPerformanceLevel(biz.improvement);
                                            return (
                                                <Grid item xs={12} md={6} lg={4} key={biz.googleLocationId || `kw-${rankIdx}`}>
                                                    <Card sx={{
                                                        ...cardBase,
                                                        ...topBar(biz.improvement > 0 ? 'linear-gradient(90deg, amDesignColors.success, #66bb6a)' : biz.improvement < 0 ? 'linear-gradient(90deg, amDesignColors.error, #ef5350)' : 'linear-gradient(90deg, amDesignColors.text.tertiary, amDesignColors.border)'),
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        border: `1px solid ${biz.improvement > 0 ? '#e8f5e8' : biz.improvement < 0 ? '#ffebee' : amDesignColors.divider}`,
                                                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                                        '&:hover': { boxShadow: '0 20px 40px rgba(0,0,0,0.12)', transform: 'translateY(-8px)' }
                                                    }}>
                                                        <CardContent sx={{ p: 3, flex: 1 }}>
                                                            {/* Rank + improvement */}
                                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                                <Avatar sx={{
                                                                    background: biz.improvement > 0 ? 'linear-gradient(135deg, amDesignColors.success, #2e7d32)' : biz.improvement < 0 ? 'linear-gradient(135deg, amDesignColors.error, #c62828)' : 'linear-gradient(135deg, amDesignColors.text.tertiary, #616161)',
                                                                    boxShadow: biz.improvement > 0 ? '0 4px 12px rgba(76,175,80,0.3)' : 'none',
                                                                    fontWeight: 'bold', fontSize: '0.9rem', color: amDesignColors.white, width: 40, height: 40
                                                                }}>
                                                                    #{rankIdx + 1}
                                                                </Avatar>
                                                                <ImprovementChip value={biz.improvement} />
                                                            </Box>

                                                            {/* Business name */}
                                                            <Typography variant="h6" sx={{ fontWeight: 700, color: amDesignColors.text.primary, mb: 0.5, fontSize: '1.05rem', lineHeight: 1.3 }}>
                                                                {biz.businessName}
                                                            </Typography>

                                                            {/* Keyword badge */}
                                                            <Chip
                                                                label={`🔑 ${kwResults.keyword}`}
                                                                size="small"
                                                                sx={{ background: '#ede7f6', color: '#4527a0', fontWeight: 600, fontSize: '0.68rem', height: 22, mb: 2 }}
                                                            />

                                                            <Chip label={perf.label} size="small" sx={{ background: `${perf.color}20`, color: perf.color, fontWeight: 600, fontSize: '0.68rem', height: 20, mb: 2, ml: 0.5 }} />

                                                            {/* Rank comparison */}
                                                            <Box sx={{ background: 'linear-gradient(135deg, amDesignColors.paper, #f5f5f5)', borderRadius: '10px', p: 2, border: '1px solid #eaeaea', mb: 2 }}>
                                                                <Grid container spacing={1}>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            {kwResults.months}M zuvor
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={biz.previousRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={6} sx={{ textAlign: 'center' }}>
                                                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontSize: '0.7rem' }}>
                                                                            Aktuell
                                                                        </Typography>
                                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                            <RankBadge rank={biz.currentRank} />
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>

                                                            <LinearProgress
                                                                variant="determinate"
                                                                value={Math.min(Math.abs(biz.improvement) * 10, 100)}
                                                                sx={{ height: 8, borderRadius: 4, backgroundColor: amDesignColors.border, '& .MuiLinearProgress-bar': { background: biz.improvement > 0 ? 'linear-gradient(90deg, amDesignColors.success, #66bb6a)' : biz.improvement < 0 ? 'linear-gradient(90deg, amDesignColors.error, #ef5350)' : 'linear-gradient(90deg, amDesignColors.text.tertiary, amDesignColors.border)', borderRadius: 4 } }}
                                                            />

                                                            {biz.trend && biz.trend.length > 0 && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block', textAlign: 'center', mt: 1 }}>
                                                                    {biz.trend.length} Datenpunkte
                                                                </Typography>
                                                            )}
                                                        </CardContent>
                                                        {biz.googleLocationId && (
                                                            <Box sx={{ borderTop: '1px solid #eee', p: 2, pt: 1.5, display: 'flex', gap: 1 }}>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    endIcon={<OpenInNew fontSize="small" />}
                                                                    onClick={() => window.open(`/ranking/${biz.googleLocationId}?keyword=${encodeURIComponent(kwResults.keyword)}`, '_blank')}
                                                                    sx={{ fontSize: '0.75rem' }}
                                                                >
                                                                    View Ranking
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Card>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>

                                    {Math.ceil(kwResults.businesses.length / KW_PER_PAGE) > 1 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                            <Pagination count={Math.ceil(kwResults.businesses.length / KW_PER_PAGE)} page={kwPage} onChange={(_, v) => setKwPage(v)} color="primary" showFirstButton showLastButton />
                                        </Box>
                                    )}
                                </>
                            )}

                            {!kwLoading && kwKeyword && (!kwResults || kwResults.businesses.length === 0) && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Keine Daten gefunden</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Für das Keyword „<strong>{kwKeyword}</strong>" wurden keine Ranking-Daten gefunden.
                                    </Typography>
                                </CardContent></Card>
                            )}

                            {!kwLoading && !kwKeyword && !kwResults && (
                                <Card sx={cardBase}><CardContent sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Keyword eingeben</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Geben Sie ein Keyword ein, um alle Unternehmen mit deren Ranking-Verbesserungen zu sehen.
                                    </Typography>
                                </CardContent></Card>
                            )}
                        </Box>
                    )}

            </Box>
        </Box>
    );
};

export default KeywordPerformancePage;
